<template>
  <div class="container py-14">
    <RouterLink
      :to="`/projects/pnszh${
        global.resources?.filters ? `?filters=${global.resources.filters}` : ''
      }`"
      class="flex items-center mb-8"
    >
      <IconChevronLeft class="size-4 mt-0.5" />
      <span>На Севере — жить!</span>
    </RouterLink>
    <LoadingView :loading="isLoading" :error="error" class="min-h-[70vh]">
      <div v-if="data" class="flex flex-wrap -mx-4">
        <div class="w-full md:w-7/12 lg:w-8/12 px-4 mb-4 md:mb-0">
          <LoadingView :loading="isLoading">
            <UIHeading :title="data.title" tag="1" class="mb-3" />
            <div class="flex items-center justify-between mb-3">
              <Reactions
                class="ml-auto"
                :global="data.reactions.global"
                :personal="data.reactions.personal"
                position="left"
                @set-reaction="
                  (val) =>
                    setReaction(
                      `/improvements/${route.params.id}/react`,
                      val,
                      'improvements',
                    )
                "
              />
            </div>

            <Gallery :media="data.media" />

            <div v-html="data.description" :class="proseContent" />

            <div
              class="relative rounded-lg overflow-hidden"
              v-if="getMedia(data.media, 'results', null).length === 2"
            >
              <span
                class="left-0 z-10 absolute text-base text-white bg-black bg-opacity-70 px-4 py-1 rounded-br-lg"
                v-text="'Было'"
              />
              <span
                class="right-0 z-10 absolute text-base text-white bg-black bg-opacity-70 px-4 py-1 rounded-bl-lg"
                v-text="'Стало'"
              />

              <BeforeAfter
                class="w-full"
                :before-image="getMedia(data.media, 'results', null)[0].path"
                :after-image="getMedia(data.media, 'results', null)[1].path"
              />
            </div>

            <ul
              role="list"
              class="grid gap-8 sm:grid-cols-2 xl:col-span-2 my-14"
            >
              <li v-for="person in data.persons" :key="person.name">
                <OrganizationTile
                  :label="labels[person.role]"
                  :context="person"
                />
              </li>
            </ul>

            <div class="flex justify-between mt-6">
              <div v-if="getMediaPath(data.media, 'passport')">
                <div
                  class="text-xs text-black-600 dark:text-black-300 tracking-widest uppercase mb-1"
                  v-text="'Узнать больше о проекте'"
                />
                <a
                  class="text-primary"
                  :href="getMediaPath(data.media, 'passport')"
                  target="_blank"
                  download
                  v-text="'Скачать презентацию'"
                />
              </div>
              <div v-if="data" class="ml-auto">
                <div>
                  <div
                    class="text-xs text-black-600 dark:text-black-300 tracking-widest uppercase mb-2"
                    v-text="'Поделиться'"
                  />

                  <ShareButtons :data="socialNetworkShareData" />
                </div>
              </div>
            </div>
          </LoadingView>
        </div>
        <aside class="w-full md:w-5/12 lg:w-4/12 px-4">
          <div class="grid gap-3">
            <div
              v-if="data.status"
              class="inline-flex rounded-lg items-center text-white shadow px-6 py-2"
              :style="`background: ${data.status.color};`"
            >
              <IconListDetails class="mr-2 flex-none w-6 h-6" />
              <span v-text="data.status.title" />
            </div>
            <div :class="cardAside">
              <ul class="space-y-5">
                <li
                  v-if="data.starts_at || data.finishes_at"
                  class="flex items-center"
                >
                  <IconCalendarClock class="mr-2 w-6 h-6 text-black-400" />
                  <ConditionalDisplaying
                    type="date"
                    format="D MMMM YYYY"
                    :from="data.starts_at"
                    :to="data.finishes_at"
                  />
                </li>
                <li v-if="data.address">
                  <div
                    class="flex items-start cursor-pointer"
                    @click="
                      callMapModal([data.address], {
                        zoom: 15,
                        title: data.address.title,
                      })
                    "
                  >
                    <IconMapPin
                      class="mr-2 w-6 h-6 text-primary dark:text-brand-1"
                    />
                    <span
                      class="text-primary dark:text-brand-1"
                      v-text="`${data.address.title} (показать на карте)`"
                    />
                  </div>
                </li>
                <li v-if="data.type" class="flex items-center">
                  <IconHome class="mr-2 w-6 h-6 text-black-400" />
                  <span v-text="data.type.title" />
                </li>
              </ul>
            </div>
            <div v-if="data.budget && data.budget.length" :class="cardAside">
              <UIHeading title="Финансирование проекта" tag="4" class="mb-3" />

              <ul class="space-y-2">
                <li v-for="(item, i) in data.budget" :key="i">
                  <div class="" v-text="item.label" />
                  <div
                    class="font-bold text-lg text-primary dark:text-brand-1"
                    v-text="numberWithCommas(item.value)"
                  />
                </li>
              </ul>
            </div>
            <div v-if="data.results && data.results.length" :class="cardAside">
              <UIHeading title="Результаты" tag="4" class="mb-3" />

              <ul class="space-y-2">
                <li v-for="(item, i) in data.results" :key="i">
                  <div class="" v-text="item.label" />
                  <div
                    class="font-bold text-lg"
                    v-text="numberWithCommas(item.value)"
                  />
                </li>
              </ul>
            </div>
          </div>
        </aside>
        <div class="w-full md:w-7/12 lg:w-8/12 px-4">
          <Comments
            class="mt-12"
            :id="route.params.id"
            :dependant="!isLoading"
            root="improvements"
            :endpoint="`/improvements/${route.params.id}/comments`"
          />
        </div>
      </div>
    </LoadingView>
  </div>
  <UIFooter />
</template>

<script setup>
import { useSeoMeta } from 'unhead'
import { computed, inject } from 'vue'
import { useQuery, useQueryClient } from 'vue-query'
import { useRoute, useRouter } from 'vue-router'

import { useAuthStore } from '@/store/auth.js'
import { useGlobalStore } from '@/store/global.js'

import {
  IconCalendarClock,
  IconChevronLeft,
  IconHome,
  IconListDetails,
  IconMapPin,
} from '@tabler/icons-vue'

import Comments from '@/components/comments/Comments.vue'
import BeforeAfter from '@/components/common/BeforeAfter.vue'
import ConditionalDisplaying from '@/components/common/ConditionalDisplaying.vue'
import Gallery from '@/components/common/Gallery.vue'
import OrganizationTile from '@/components/common/OrganizationTile.vue'
import LoadingView from '@/components/loading/LoadingView.vue'
import Reactions from '@/components/Reactions.vue'
import ShareButtons from '@/components/share/ShareButtons.vue'
import UIFooter from '@/components/ui/UIFooter.vue'
import UIHeading from '@/components/ui/UIHeading.vue'
import { callMapModal, numberWithCommas } from '@/utils/global.js'
import { getMedia, getMediaPath } from '@/utils/media.js'
import { useReactions } from '@/utils/reactions.js'
import { cardAside, proseContent } from '@/utils/ui.js'
import { usePageId } from '@/utils/use/usePageId.js'

const {
  route,
  http,
  client,
  auth,
  global,
  pageState: { isLoading, isError, data, error },
} = usePageId({
  rootKey: 'improvements',
  queryKey: 'improvements',
})

const router = useRouter()

if (route.query.linkstat) {
  http(`/improvements/${route.params.id}/stat`, {
    method: 'POST',
  })
}

const { setReaction } = useReactions({
  queryClient: client,
  auth,
  queryKey: ['improvements', route.params.id],
  rootKey: 'improvements',
  rootParamId: route.params.id,
})

const labels = {
  author: 'Автор',
  customer: 'Заказчик',
  executor: 'Исполнитель',
  responsible: 'Ответственный',
}

const socialNetworkShareData = computed(() => {
  const path = new URL(location.href)
  if (auth.user) {
    path.searchParams.append('referral', auth.user.referral)
  }

  return {
    title: data.value?.title,
    url: path.toString(),
    media: getMediaPath(data.value?.media, 'default-optimized'),
  }
})
</script>
